"use client";

import { type ReactNode, useState } from "react";
import {
  ErrorNotice,
  Field,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { GasLawAnswer } from "@/components/tools/chem-physical/gas-law-answer";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  GAS_UNIT_OPTIONS,
  type GasTerm,
} from "@/lib/tool-kit/chem-physical/gas";
import {
  entryKey,
  entrySymbol,
  GAS_LAW_IDS,
  GAS_LAWS,
  type GasLaw,
  type GasLawFailure,
  lawEquation,
  type StateNumber,
  solveGasLaw,
  type UnknownSpec,
} from "@/lib/tool-kit/chem-physical/gas-laws";
import { fewestSigFigs } from "@/lib/tool-kit/chemistry/format";

/** English strings. A translated content file overrides these through `ui`. */
export const COMBINED_GAS_LAW_CALCULATOR_STRINGS = {
  lawLabel: "Which gas law?",
  lawCombined: "Combined gas law",
  lawBoyle: "Boyle's law",
  lawCharles: "Charles's law",
  lawGayLussac: "Gay-Lussac's law",
  lawAvogadro: "Avogadro's law",
  noteCombined:
    "The amount of gas is fixed; pressure, volume and temperature all change. Boyle's, Charles's and Gay-Lussac's laws are this law with one variable held constant.",
  noteBoyle:
    "Temperature and amount are constant. Pressure and volume are inversely proportional: squeeze the gas and the pressure rises.",
  noteCharles:
    "Pressure and amount are constant. Volume is directly proportional to the absolute temperature.",
  noteGayLussac:
    "Volume and amount are constant. Pressure is directly proportional to the absolute temperature.",
  noteAvogadro:
    "Pressure and temperature are constant. Volume is directly proportional to the amount of gas.",
  solveLabel: "Solve for",
  stateInitial: "Initial state",
  stateFinal: "Final state",
  unitsHeading: "Units",
  unitsHint:
    "Both states share each unit. Temperature is converted to kelvin first; the other units cancel in the ratio.",
  solvedCell: "Solved below",
  fieldLabel: "{name} ({symbol})",
  solveOption: "{state}: {name} ({symbol})",
  termP: "Pressure",
  termV: "Volume",
  termT: "Temperature",
  termN: "Amount of gas",
  unitP: "Pressure unit",
  unitV: "Volume unit",
  unitT: "Temperature unit",
  unitN: "Amount unit",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  answerLabel: "Solved: {symbol}",
  lawUsed: "Law used",
  working: "The working",
  convertLine: "{symbol}: {value} {unit} = {converted} K",
  substituted: "{symbol} = {expression} = {result} {unit}",
  answerLine: "{result} K = {answer}",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{symbol} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{symbol} must be greater than zero.",
  errorBelowZero:
    "{symbol} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible. Gas-law temperatures are absolute.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{symbol} = {value}",
} as const;

type Strings = typeof COMBINED_GAS_LAW_CALCULATOR_STRINGS;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const TERM_ORDER: GasTerm[] = ["p", "v", "t", "n"];

const START: Record<string, string> = {
  p1: "1.00",
  v1: "5.00",
  t1: "20.0",
  n1: "1.00",
  p2: "2.00",
  v2: "",
  t2: "100.",
  n2: "2.00",
};

const START_UNITS: Record<GasTerm, string> = {
  p: "atm",
  v: "L",
  t: "C",
  n: "mol",
};

function failureMessage(failure: GasLawFailure, strings: Strings): string {
  if (failure.reason === "missing") {
    return strings.enterValues;
  }
  const template = {
    unreadable: strings.errorUnreadable,
    nonPositive: strings.errorNonPositive,
    belowZero: strings.errorBelowZero,
  }[failure.reason];
  return t(template, { symbol: entrySymbol(failure.term, failure.state) });
}

export default function CombinedGasLawCalculatorWidget() {
  const strings = useToolUi(COMBINED_GAS_LAW_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [law, setLaw] = useState<GasLaw>("combined");
  const [chosen, setChosen] = useState<UnknownSpec>({ term: "v", state: 2 });
  const [text, setText] = useState(START);
  const [unit, setUnit] = useState(START_UNITS);
  const [sigChoice, setSigChoice] = useState("match");

  const terms = TERM_ORDER.filter((term) =>
    GAS_LAWS[law].some((entry) => entry.term === term)
  );
  // A law that does not use the chosen variable falls back to the final volume or pressure.
  const unknown: UnknownSpec = terms.includes(chosen.term)
    ? chosen
    : { term: terms[0], state: 2 };

  const names: Record<GasTerm, string> = {
    p: strings.termP,
    v: strings.termV,
    t: strings.termT,
    n: strings.termN,
  };
  const unitLabels: Record<GasTerm, string> = {
    p: strings.unitP,
    v: strings.unitV,
    t: strings.unitT,
    n: strings.unitN,
  };
  const stateNames: Record<StateNumber, string> = {
    1: strings.stateInitial,
    2: strings.stateFinal,
  };
  const notes: Record<GasLaw, string> = {
    combined: strings.noteCombined,
    boyle: strings.noteBoyle,
    charles: strings.noteCharles,
    gayLussac: strings.noteGayLussac,
    avogadro: strings.noteAvogadro,
  };
  const lawNames: Record<GasLaw, string> = {
    combined: strings.lawCombined,
    boyle: strings.lawBoyle,
    charles: strings.lawCharles,
    gayLussac: strings.lawGayLussac,
    avogadro: strings.lawAvogadro,
  };

  const outcome = solveGasLaw({ law, unknown, text, unit });
  const typed = terms.flatMap((term) =>
    ([1, 2] as const)
      .filter((state) => !(unknown.term === term && unknown.state === state))
      .map((state) => text[entryKey(term, state)])
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  let body: ReactNode;
  if (outcome.ok) {
    body = (
      <GasLawAnswer
        sig={sig}
        solution={outcome}
        strings={strings}
        texts={text}
        units={unit}
      />
    );
  } else if (outcome.reason === "missing") {
    body = <Notice>{failureMessage(outcome, strings)}</Notice>;
  } else {
    body = <ErrorNotice>{failureMessage(outcome, strings)}</ErrorNotice>;
  }

  const solveOptions = ([2, 1] as const).flatMap((state) =>
    terms.map((term) => ({
      value: entryKey(term, state),
      label: t(strings.solveOption, {
        state: stateNames[state],
        name: names[term],
        symbol: entrySymbol(term, state),
      }),
    }))
  );

  const stateColumn = (state: StateNumber) => (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{stateNames[state]}</h3>
      {terms.map((term) => {
        const key = entryKey(term, state);
        const label = t(strings.fieldLabel, {
          name: names[term],
          symbol: entrySymbol(term, state),
        });
        if (unknown.term === term && unknown.state === state) {
          return (
            <Field id={`cgl-${key}`} key={key} label={label}>
              <p
                className="rounded-md border border-primary/40 border-dashed bg-primary/5 px-3 py-2 text-foreground text-sm"
                id={`cgl-${key}`}
              >
                {strings.solvedCell}
              </p>
            </Field>
          );
        }
        return (
          <TextField
            id={`cgl-${key}`}
            key={key}
            label={label}
            numeric
            onChange={(value) => setText({ ...text, [key]: value })}
            value={text[key]}
          />
        );
      })}
    </div>
  );

  return (
    <>
      <PillGroup
        label={strings.lawLabel}
        name="cgl-law"
        onChange={setLaw}
        options={GAS_LAW_IDS.map((id) => ({ value: id, label: lawNames[id] }))}
        value={law}
      />
      <div className="space-y-1">
        <p className="font-mono text-foreground text-sm">{lawEquation(law)}</p>
        <p className={HINT}>{notes[law]}</p>
      </div>
      <SelectField
        id="cgl-unknown"
        label={strings.solveLabel}
        onChange={(value) =>
          setChosen({
            term: value.charAt(0) as GasTerm,
            state: Number(value.charAt(1)) as StateNumber,
          })
        }
        options={solveOptions}
        value={entryKey(unknown.term, unknown.state)}
      />
      <div className="grid gap-6 sm:grid-cols-2">
        {stateColumn(1)}
        {stateColumn(2)}
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.unitsHeading}</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          {terms.map((term) => (
            <SelectField
              id={`cgl-unit-${term}`}
              key={term}
              label={unitLabels[term]}
              onChange={(value) => setUnit({ ...unit, [term]: value })}
              options={GAS_UNIT_OPTIONS[term]}
              value={unit[term]}
            />
          ))}
          <SelectField
            id="cgl-sig"
            label={strings.sigFigs}
            onChange={setSigChoice}
            options={[
              { value: "match", label: strings.sigMatch },
              ...SIG_CHOICES.map((value) => ({
                value,
                label: tPlural(strings.sigChoice, locale, Number(value), {
                  count: fmt(Number(value)),
                }).trim(),
              })),
            ]}
            value={sigChoice}
          />
        </div>
        <p className={HINT}>{strings.unitsHint}</p>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
